import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
// Eagerly load only what the landing page needs on first paint.
// Everything else (FR resources + dashboard/auth/legal/contact/portal namespaces)
// is loaded on-demand to keep the landing's initial JS payload small.
import enCommon from "@/locales/en/common.json";
import enLanding from "@/locales/en/landing.json";

export const SUPPORTED_LANGUAGES = ["en", "fr"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        landing: enLanding,
      },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true, // "fr-FR" -> "fr"
    defaultNS: "common",
    // Declare every namespace so useTranslation('auth') etc. resolve once loaded.
    ns: ["common", "landing", "auth", "legal", "contact", "portal", "dashboard"],
    partialBundledLanguages: true,
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "docuchase-lang",
      caches: ["localStorage"],
    },
    react: {
      useSuspense: false,
    },
  });

/* ------------------------------------------------------------------ */
/*  Lazy + idle namespace loading                                      */
/* ------------------------------------------------------------------ */

const loadedBundles = new Set<string>(["en:common", "en:landing"]);

const enLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  auth: () => import("@/locales/en/auth.json"),
  legal: () => import("@/locales/en/legal.json"),
  contact: () => import("@/locales/en/contact.json"),
  portal: () => import("@/locales/en/portal.json"),
  dashboard: () => import("@/locales/en/dashboard.json"),
};

const frLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  common: () => import("@/locales/fr/common.json"),
  landing: () => import("@/locales/fr/landing.json"),
  auth: () => import("@/locales/fr/auth.json"),
  legal: () => import("@/locales/fr/legal.json"),
  contact: () => import("@/locales/fr/contact.json"),
  portal: () => import("@/locales/fr/portal.json"),
  dashboard: () => import("@/locales/fr/dashboard.json"),
};

async function loadBundle(lng: "en" | "fr", ns: string): Promise<void> {
  const key = `${lng}:${ns}`;
  if (loadedBundles.has(key)) return;
  const loader = (lng === "en" ? enLoaders : frLoaders)[ns];
  if (!loader) return;
  loadedBundles.add(key); // mark optimistically to dedupe in-flight
  try {
    const mod = await loader();
    i18n.addResourceBundle(lng, ns, mod.default, true, true);
  } catch (err) {
    loadedBundles.delete(key);
    console.warn(`Failed to load i18n bundle ${key}`, err);
  }
}

export async function ensureNamespaces(namespaces: string[]): Promise<void> {
  const lng = (i18n.language?.startsWith("fr") ? "fr" : "en") as "en" | "fr";
  await Promise.all(namespaces.map((ns) => loadBundle(lng, ns)));
  // Also make sure EN fallback exists for non-default namespaces
  if (lng !== "en") {
    await Promise.all(namespaces.map((ns) => loadBundle("en", ns)));
  }
}

/**
 * Preload an explicit target language's namespaces. Used by the language
 * switcher so we never flip i18n.language to "fr" before the FR JSON has
 * arrived (which would briefly render raw keys / blank labels).
 */
export async function preloadLanguage(
  target: "en" | "fr",
  namespaces: string[],
): Promise<void> {
  await Promise.all(namespaces.map((ns) => loadBundle(target, ns)));
  // EN is the fallback — make sure it's available too.
  if (target !== "en") {
    await Promise.all(namespaces.map((ns) => loadBundle("en", ns)));
  }
}


// When the language changes (e.g. user switches to FR), make sure the
// resources for that language are loaded for every active namespace.
i18n.on("languageChanged", (lng) => {
  const target = (lng?.startsWith("fr") ? "fr" : "en") as "en" | "fr";
  if (target === "en") return;
  const active = i18n.options.ns as string[];
  active.forEach((ns) => void loadBundle(target, ns));
});

// After first paint, idle-load the remaining EN namespaces so navigations
// to /login, /signup, /dashboard, etc. don't have to wait on a fetch.
if (typeof window !== "undefined") {
  const idle = (cb: () => void) => {
    type IdleWin = Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    const w = window as IdleWin;
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(cb, { timeout: 2500 });
    } else {
      setTimeout(cb, 1500);
    }
  };
  idle(() => {
    Object.keys(enLoaders).forEach((ns) => void loadBundle("en", ns));
  });
}

export default i18n;
